/**
 * InterviewDialog (Feature A1)
 * ============================
 * Lets a user ask a follow-up question to:
 *   - a single specific persona ("single" mode), or
 *   - a random cohort of personas ("cohort" mode, optionally filtered by
 *     original response).
 *
 * The dialog also lists past interview sessions for the simulation so
 * results can be reviewed without re-burning tokens.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Loader2, MessageCircle, Trash2, Users, User, Clock } from 'lucide-react';
import { toast } from 'sonner';

const MODE_SINGLE = 'single';
const MODE_COHORT = 'cohort';

const attrPreview = (attrs) => {
  if (!attrs) return '';
  const entries = Object.entries(attrs).filter(([k]) => k !== 'id');
  return entries.slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(' · ');
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export default function InterviewDialog({ open, onOpenChange, simulation, stimulus }) {
  const [mode, setMode] = useState(MODE_SINGLE);
  const [candidates, setCandidates] = useState([]);
  const [selectedIndividualId, setSelectedIndividualId] = useState('');
  const [filterResponse, setFilterResponse] = useState('all');
  const [sampleSize, setSampleSize] = useState(5);
  const [question, setQuestion] = useState('');
  const [running, setRunning] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const responseScale = stimulus?.response_scale || [];
  const simulationId = simulation?.id;

  // Reset ephemeral state whenever dialog opens
  useEffect(() => {
    if (!open) return;
    setCurrentSession(null);
    setQuestion('');
    setSelectedIndividualId('');
    setFilterResponse('all');
    setSampleSize(5);
    setMode(MODE_SINGLE);
  }, [open, simulationId]);

  // Load candidates + history when opened
  useEffect(() => {
    if (!open || !simulationId) return;
    let alive = true;
    (async () => {
      try {
        const [candRes, histRes] = await Promise.all([
          api.get(`/simulations/${simulationId}/interview-candidates?limit=50`),
          api.get(`/simulations/${simulationId}/interviews?limit=20`),
        ]);
        if (!alive) return;
        setCandidates(candRes.data || []);
        setHistory(histRes.data || []);
      } catch (e) {
        if (alive) toast.error('Could not load interview data');
      }
    })();
    return () => { alive = false; };
  }, [open, simulationId]);

  const reloadHistory = async () => {
    if (!simulationId) return;
    setHistoryLoading(true);
    try {
      const r = await api.get(`/simulations/${simulationId}/interviews?limit=20`);
      setHistory(r.data || []);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRun = async () => {
    if (!question.trim() || question.trim().length < 3) {
      toast.error('Please enter a follow-up question (at least 3 characters)');
      return;
    }
    if (mode === MODE_SINGLE && !selectedIndividualId) {
      toast.error('Please pick a respondent');
      return;
    }
    setRunning(true);
    setCurrentSession(null);
    try {
      const url = mode === MODE_SINGLE
        ? `/simulations/${simulationId}/interview`
        : `/simulations/${simulationId}/interview-all`;
      const payload = mode === MODE_SINGLE
        ? { individual_id: selectedIndividualId, question: question.trim() }
        : {
            question: question.trim(),
            sample_size: sampleSize,
            ...(filterResponse && filterResponse !== 'all' ? { filter_response: filterResponse } : {}),
          };
      const res = await api.post(url, payload);
      setCurrentSession(res.data);
      toast.success(mode === MODE_SINGLE ? 'Interview complete' : `Cohort interview complete (${res.data.sample_size} personas)`);
      reloadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Interview failed');
    } finally {
      setRunning(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await api.delete(`/simulations/${simulationId}/interviews/${sessionId}`);
      toast.success('Session deleted');
      if (currentSession?.id === sessionId) setCurrentSession(null);
      reloadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const selectedCandidate = useMemo(
    () => candidates.find(c => c.individual_id === selectedIndividualId),
    [candidates, selectedIndividualId]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="interview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-blue-600" />
            Interview a Respondent
          </DialogTitle>
          <DialogDescription>
            Ask a follow-up question to personas from <span className="font-medium text-slate-700">{simulation?.name}</span>. Same AI engine, conditioned on each persona's attributes and prior answer.
          </DialogDescription>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="flex gap-2 border-b pb-3">
          <Button
            size="sm"
            variant={mode === MODE_SINGLE ? 'default' : 'outline'}
            onClick={() => setMode(MODE_SINGLE)}
            className="flex items-center gap-1.5"
            data-testid="interview-mode-single"
          >
            <User className="w-3.5 h-3.5" /> Single Respondent
          </Button>
          <Button
            size="sm"
            variant={mode === MODE_COHORT ? 'default' : 'outline'}
            onClick={() => setMode(MODE_COHORT)}
            className="flex items-center gap-1.5"
            data-testid="interview-mode-cohort"
          >
            <Users className="w-3.5 h-3.5" /> Cohort (Random Sample)
          </Button>
        </div>

        {/* Mode-specific controls */}
        {mode === MODE_SINGLE ? (
          <div className="space-y-2">
            <Label>Pick a respondent</Label>
            <Select value={selectedIndividualId} onValueChange={setSelectedIndividualId}>
              <SelectTrigger data-testid="interview-persona-select">
                <SelectValue placeholder={`Choose from ${candidates.length} respondents`} />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {candidates.map(c => (
                  <SelectItem key={c.individual_id} value={c.individual_id}>
                    <span className="text-xs">
                      <Badge variant="secondary" className="mr-2">{c.response}</Badge>
                      {attrPreview(c.attributes)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedCandidate && (
              <div className="text-xs text-slate-600 bg-slate-50 rounded-md px-3 py-2">
                <div><span className="font-medium">Original answer:</span> {selectedCandidate.response}</div>
                <div><span className="font-medium">Demographics:</span> {attrPreview(selectedCandidate.attributes)}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Filter by original response (optional)</Label>
              <Select value={filterResponse} onValueChange={setFilterResponse}>
                <SelectTrigger data-testid="interview-cohort-filter">
                  <SelectValue placeholder="All respondents" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All respondents</SelectItem>
                  {responseScale.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sample size (1 – 50)</Label>
              <Input
                type="number"
                min={1}
                max={50}
                value={sampleSize}
                onChange={e => setSampleSize(Math.min(50, Math.max(1, parseInt(e.target.value || '1', 10))))}
                data-testid="interview-cohort-sample-size"
              />
            </div>
          </div>
        )}

        {/* Question */}
        <div className="space-y-2">
          <Label>Follow-up question</Label>
          <Textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder={mode === MODE_SINGLE
              ? "e.g. What would change your answer? What's the most important factor for you?"
              : "e.g. If we reduced the price by 20%, would that change how you feel?"}
            rows={3}
            maxLength={2000}
            data-testid="interview-question"
          />
          <div className="text-[11px] text-slate-400 text-right">{question.length}/2000</div>
        </div>

        {/* Run */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="interview-close">Close</Button>
          <Button onClick={handleRun} disabled={running} data-testid="interview-run">
            {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Interviewing…</> : <>Run Interview</>}
          </Button>
        </div>

        {/* Current session results */}
        {currentSession && (
          <div className="space-y-3 border-t pt-4" data-testid="interview-results">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Results ({currentSession.sample_size} response{currentSession.sample_size === 1 ? '' : 's'})</h3>
              <Badge variant="outline" className="text-[10px]">{currentSession.mode}</Badge>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {(currentSession.responses || []).map((r, i) => (
                <div key={i} className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-lg p-3 space-y-1.5">
                  <div className="flex items-center gap-2 text-[11px] text-slate-500">
                    <Badge variant="secondary" className="text-[10px]">{r.original_response || '—'}</Badge>
                    <span>·</span>
                    <span className="truncate">{attrPreview(r.attributes)}</span>
                  </div>
                  <p className="text-sm text-slate-800 italic">"{r.answer}"</p>
                  {r.reasoning && <p className="text-[11px] text-slate-500">Why: {r.reasoning}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        {history.length > 0 && !currentSession && (
          <div className="space-y-2 border-t pt-4" data-testid="interview-history">
            <h3 className="font-semibold text-slate-900 text-sm flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> Past interviews
              {historyLoading && <Loader2 className="w-3 h-3 animate-spin" />}
            </h3>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {history.map(h => (
                <div key={h.id} className="flex items-center justify-between gap-2 text-xs bg-slate-50 rounded-md px-3 py-2 hover:bg-slate-100 cursor-pointer" onClick={() => setCurrentSession(h)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px]">{h.mode}</Badge>
                      <span className="text-[10px] text-slate-400">{h.sample_size} resp · {timeAgo(h.created_at)}</span>
                    </div>
                    <div className="truncate text-slate-700 mt-0.5">{h.question}</div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                    onClick={(e) => { e.stopPropagation(); handleDeleteSession(h.id); }}
                    data-testid={`interview-history-delete-${h.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
